import React from 'react'

enum Keys {
  Enter = 'Enter',
  Space = ' ',
  Escape = 'Escape'
}

type SimpleKeyboardEvent = React.KeyboardEvent<Element> | KeyboardEvent

type TriggerOptions = {
  allowDefault?: boolean
}

function hasModifiers(event: SimpleKeyboardEvent) {
  return event.altKey || event.shiftKey || event.ctrlKey || event.metaKey
}

/**
 * @returns Whether event represents a keystroke that should execute an anchor's trigger.
 */
export function isAnchorTriggerKey(event: SimpleKeyboardEvent) {
  return event.key === Keys.Enter && !hasModifiers(event)
}

/**
 * @returns Whether event represents a keystroke that should execute an button's trigger.
 */
export function isButtonTriggerKey(event: SimpleKeyboardEvent) {
  return (
    (event.key === Keys.Enter || event.key === Keys.Space) &&
    !hasModifiers(event)
  )
}

function isEscapeKey(event: SimpleKeyboardEvent) {
  return event.key === Keys.Escape && !hasModifiers(event)
}

function onKeyMatched(
  keyMatcher: (e: SimpleKeyboardEvent) => boolean,
  callback?: (e?: SimpleKeyboardEvent) => void | undefined,
  options: TriggerOptions = {}
) {
  if (!callback) {
    return callback
  }
  return (event: SimpleKeyboardEvent) => {
    if (keyMatcher(event)) {
      if (!options.allowDefault) {
        event.preventDefault()
      }
      callback(event)
    }
  }
}

/**
 * Does not call `e.preventDefault()`.
 * @param callback Trigger associated with anchor-like HTMLElement.
 * @returns A function to use in keyboard event listeners (i.e. keyup, keydown)
 * which executes `callback` when a keystroke would normally execute an anchor's trigger
 * (i.e. enter).
 */
export function onAnchorIntercept(
  callback?: (e?: SimpleKeyboardEvent) => void | undefined
) {
  return onKeyMatched(isAnchorTriggerKey, callback, {
    allowDefault: true
  })
}

/**
 * Calls `e.preventDefault()` when keyboard event qualifies as *anchor* event.
 * @param callback Trigger associated with anchor-like HTMLElement.
 * @returns A function to use in keyboard event listeners (i.e. keyup, keydown)
 * which executes `callback` when a keystroke would normally execute an anchor's trigger
 * (i.e. enter).
 */
export function onAnchorTriggerKey(
  callback?: (e?: SimpleKeyboardEvent) => void | undefined
) {
  return onKeyMatched(isAnchorTriggerKey, callback)
}

/**
 * @param callback Trigger associated with button-like HTMLElement.
 * @returns A function to use in keyboard event listeners (i.e. keyup, keydown)
 * which executes `callback` when a keystroke would normally execute a button's trigger
 * (i.e. enter, space).
 */
export function onButtonTriggerKey(
  callback?: (e?: SimpleKeyboardEvent) => void | undefined
) {
  return onKeyMatched(isButtonTriggerKey, callback)
}

/**
 * @param callback Trigger associated with tab-like HTMLElement.
 * @returns A function to use in keyboard event listeners (i.e. keyup, keydown)
 * which executes `callback` when a keystroke would normally execute a tab's trigger
 * (i.e. enter, space).
 */
export function onTabTriggerKey(
  callback?: (e?: SimpleKeyboardEvent) => void | undefined
) {
  return onKeyMatched(isButtonTriggerKey, callback)
}

/**
 * @param callback Trigger associated with Esc key.
 * @returns A function to use in keyboard event listeners (i.e. keyup, keydown)
 * which executes `callback` when a keystroke corresponds to the Esc key.
 */
export function onEscapeKey(
  callback?: (e?: SimpleKeyboardEvent) => void | undefined
) {
  return onKeyMatched(isEscapeKey, callback)
}

export const testables = {
  onKeyMatched
}
